import React, { useEffect } from 'react';
import { useLatestRef } from '../hooks/useLatestRef';

interface ToastProps {
  message: string;
  type?: 'success' | 'error' | 'info';
  isVisible: boolean;
  onClose: () => void;
  duration?: number;
}

const typeStyles: Record<string, string> = {
  success: 'bg-stone-900 text-white',
  error: 'bg-red-700 text-white',
  info: 'bg-stone-700 text-white',
};

const Toast: React.FC<ToastProps> = ({
  message,
  type = 'success',
  isVisible,
  onClose,
  duration: durationProp,
}) => {
  const duration = durationProp ?? (type === 'error' ? 16000 : 3000);
  const onCloseRef = useLatestRef(onClose);

  useEffect(() => {
    if (!isVisible) return;
    const timer = setTimeout(() => onCloseRef.current(), duration);
    return () => clearTimeout(timer);
  }, [isVisible, duration]);

  if (!isVisible) return null;

  const messageClass =
    type === 'error'
      ? 'text-sm font-normal normal-case tracking-normal max-w-[min(100vw-4rem,36rem)] max-h-[min(70vh,24rem)] overflow-y-auto leading-snug text-left'
      : 'text-[10px] font-bold uppercase tracking-widest';

  return (
    <div
      className={`fixed bottom-8 left-1/2 -translate-x-1/2 z-[110] px-6 py-4 shadow-2xl animate-fade-in flex items-start gap-4 ${typeStyles[type]}`}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      aria-relevant="text"
    >
      <span id="toast-message" className={messageClass}>
        {message}
      </span>
      <button
        type="button"
        onClick={onClose}
        className="text-white/60 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white/50 rounded p-1"
        aria-label="Dismiss notification"
      >
        ×
      </button>
    </div>
  );
};

export default Toast;
